import { CdkMenuModule } from '@angular/cdk/menu';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewEncapsulation,
  booleanAttribute,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { YouTubePlayer } from '@angular/youtube-player';
import { Editor } from '@tiptap/core';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import { Node } from 'prosemirror-model';
import { Transaction } from 'prosemirror-state';
import { StorageService } from '@millionshow/api-client';
import { Icon } from '../icon/icon.component';
import { fontSizes } from './consts/font-sizes.const';
import { ResizableImage } from './resizable-image';
import { CustomYoutube } from './custom-youtube';

@Component({
  selector: 'app-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, CdkMenuModule, Icon, YouTubePlayer],
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: EditorComponent,
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class EditorComponent implements AfterViewInit, ControlValueAccessor {
  baseUrl = input.required<string>();

  editorRef = viewChild<ElementRef<HTMLElement>>('editorRef');
  format = input<'html' | 'json'>('html');
  image = input<boolean, string | boolean>(true, {
    transform: booleanAttribute,
  });
  viewer = input<boolean, string | boolean>(false, {
    transform: booleanAttribute,
  });

  fontSizes = fontSizes;
  fontSize = signal<string>('16px');

  public editor: Editor | null = null;

  isCurrentNodeTable = signal<boolean>(false);

  private readonly storageService = inject(StorageService);

  ngAfterViewInit(): void {
    this.initializeEditor();
  }

  handleCommand(command: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    command();
  }

  private initializeEditor() {
    if (this.editor) return;
    this.editor = new Editor({
      element: this.editorRef()?.nativeElement,
      extensions: [
        StarterKit.configure({
          paragraph: {
            HTMLAttributes: {
              class: 'min-h-6',
            },
          },
          bulletList: {
            HTMLAttributes: {
              class: 'list-disc ml-5',
            },
          },
          orderedList: {
            HTMLAttributes: {
              class: 'list-decimal ml-5',
            },
          },
          link: {
            autolink: true,
            linkOnPaste: true,
            defaultProtocol: 'https',
            openOnClick: true,
            HTMLAttributes: {
              class: 'text-blue-500 underline',
            },
          },
        }),
        ResizableImage,
        TextStyleKit,
        TextAlign.configure({
          types: ['paragraph', 'heading', 'image', 'youtube'],
          alignments: ['left', 'center', 'right'], // 가급적 심플하게 유지
          defaultAlignment: 'center',
        }),
        Highlight.configure({
          multicolor: true,
        }),
        Superscript,
        Subscript,
        TableKit.configure({
          table: {
            resizable: true,
          },
        }),
        CustomYoutube.configure({
          inline: true,
        }),
      ],
      onTransaction: ({ editor, transaction }) => {
        // check if image node was deleted
        this.handleImageDelete(transaction);
      },
      onUpdate: ({ editor }) => {
        switch (this.format()) {
          case 'html':
            this.onChange(editor.getHTML());
            break;
          case 'json':
            this.onChange(JSON.stringify(editor.getJSON()));
            break;
        }
      },
      onCreate: ({ editor }) => {
        editor.view.dom.spellcheck = false;
        editor.commands.setFontSize('16px');
        editor.commands.setTextAlign('center');
      },
    });
  }

  insertLinkWithText(url: string, linkText?: string) {
    if (!this.editor || !url) return;

    const { from, to } = this.editor.state.selection;
    const selectedText = this.editor.state.doc.textBetween(from, to);

    // 표시할 텍스트 결정 (우선순위: 선택된 텍스트 > 입력된 링크 텍스트 > URL)
    const displayText = selectedText || linkText || url;

    if (selectedText) {
      // 선택된 텍스트가 있는 경우: 해당 텍스트에 링크 적용
      this.editor
        .chain()
        .focus()
        .setLink({ href: url, target: '_blank' })
        .run();
    } else {
      // 선택된 텍스트가 없는 경우: 새 링크 삽입
      this.editor
        .chain()
        .focus()
        .insertContent(`<a href="${url}" target="_blank">${displayText}</a> `)
        .run();
    }
  }

  clearLinkInputs(...inputs: HTMLInputElement[]) {
    inputs.forEach(input => (input.value = ''));
  }

  selectImage(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const a = document.createElement('input');
    a.type = 'file';
    a.accept = 'image/*';
    a.click();
    a.onchange = async e => {
      const file = (e.target as any)?.files[0];

      if (!file) {
        return;
      }

      try {
        // 파일을 서버에 업로드
        const uploadedFiles = await new Promise<any[]>((resolve, reject) => {
          this.storageService
            .storageControllerUploadFiles({
              bucket: 'millionshow',
              body: {
                files: [file],
              },
            })
            .subscribe({
              next: res => resolve(res),
              error: err => reject(err),
            });
        });

        if (uploadedFiles && uploadedFiles.length > 0) {
          // 업로드된 파일의 URL 사용
          const imageUrl = uploadedFiles[0].url;
          this.editor
            ?.chain()
            .focus()
            .setImage({
              src: imageUrl,
            })
            .setTextAlign('center')
            .run();
        }
      } catch (error) {
        console.error('이미지 업로드 실패:', error);
      }
    };
  }

  handleImageDelete(transaction: Transaction) {
    const current: Node[] = [];
    transaction.doc.content.forEach(node => {
      if (node.type.name == 'image') {
        current.push(node);
      }
    });
    const before: Node[] = [];
    transaction.before.content.forEach(node => {
      if (node.type.name == 'image') {
        before.push(node);
      }
    });
    if (!current || before.length == 0) {
      return;
    }

    const deletedImageNodes = before.filter(node => {
      const src = node.attrs['src'];
      return !current.find(curNode => curNode.attrs['src'] == src);
    });

    if (deletedImageNodes.length > 0) {
      deletedImageNodes.forEach(async node => {
        // await this.uploadService.delete(node.attrs['src']);
      });
    }
  }

  addYoutube(src: string) {
    this.editor
      ?.chain()
      .focus()
      .createParagraphNear()
      .selectNodeForward()
      .insertContent({ type: 'youtube', attrs: { src } })
      .run();
  }

  isYoutubeUrl(url: string) {
    const isHostYoutube = (url: string) =>
      ['youtube.com', 'youtu.be'].some(host => url?.includes(host));

    const hasVideoId = (url: string) => {
      const videoId = this.getYouTubeVideoId(url);
      return !!videoId;
    };

    return isHostYoutube(url) && hasVideoId(url);
  }

  getYouTubeVideoId(url: string): string | undefined {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname;
    const pathname = parsedUrl.pathname;
    const searchParams = parsedUrl.searchParams;

    if (hostname === 'youtu.be') {
      return pathname.slice(1);
    }

    if (searchParams.has('v')) {
      return searchParams.get('v') || undefined;
    }
    if (pathname.startsWith('/live/')) {
      return pathname.split('/live/')[1]?.split('?')[0] || undefined;
    }

    return undefined;
  }

  writeValue(content: any): void {
    if (!this.editor) this.initializeEditor();
    if (this.format() === 'html') {
      // check content is html
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      if (doc.body.innerHTML !== '') {
        this.editor?.commands.setContent(content);
      } else {
        // console.error('Selected format is HTML but the content is not HTML.');
      }
      this.editor?.commands.setContent(content);
    } else if (this.format() === 'json') {
      try {
        const json = JSON.parse(content);
        this.editor?.commands.setContent(json);
      } catch (error) {
        console.error('Selected format is JSON but the content is not JSON.');
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.editor?.setEditable(!isDisabled);
  }

  onChange = (value: string) => {};
  onTouched = () => {};
}
